import ControlledInput from "Components/Common/ControlledInput";
import { StrategicPlanItem } from "Stores/strategicPlanStore";
import React, { useContext } from "react";
import { AccordionContext, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { CustomToggle } from "./CustomToggle";
import { PlusIcon } from "Components/Icons/PlusIcon";
import { ArchiveIcon } from "Components/Icons/ArchiveIcon";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import ActiveDragIcon from "Components/Icons/ActiveDrag";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { getActionPlanDrillDownUrl } from "@/common/effectivenessActionPlanDrillDown";
import type { DrillDownStatusType } from "@/common/effectivenessActionPlanDrillDown";
import { EditStrategicPlanFormContext } from "./editStrategicPlan";
import { useNavigate } from "react-router-dom";

const MenuAction = ({ onAddItem, onAddSubItem, disable }: {
    onAddItem?: () => void,
    onAddSubItem?: () => void,
    disable?: boolean
}) => {
    return (
        <Dropdown>
            <Dropdown.Toggle id="dropdown-basic">
                <PlusIcon className={disable ? "drop-icon-disabled" : "drop-icon"} />
            </Dropdown.Toggle>
            {!disable &&
                <Dropdown.Menu>
                    <Dropdown.Item onClick={onAddItem}>Add item</Dropdown.Item>
                    {onAddSubItem && <Dropdown.Item onClick={onAddSubItem}>Add sub-item</Dropdown.Item>}
                </Dropdown.Menu>
            }
        </Dropdown>
    );
};

type ItemContainerProps = {
    item?: StrategicPlanItem,
    level: number,
    onOpenArchive?: () => void,
    eventKey: string,
    onOpenDelete?: () => void,
    onAddItem?: () => void,
    onAddSubItem?: () => void,
    onAddChild?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | ((index: number) => void);
    disable?: boolean;
    enableMenu?: boolean;
    onChange?: () => void;
    controlledInput?: boolean
    inputName?: string
    required?: boolean
    hideArchive?: boolean
    defaultValue?: string,
    toggleButtonRef: React.MutableRefObject<Record<string, HTMLButtonElement | null>>
    dragHandleProps?: DraggableProvidedDragHandleProps | null | undefined
    isEdit: boolean,
}

export const ItemContainer = (
    {
        item,
        level,
        eventKey,
        onOpenArchive,
        onOpenDelete,
        onAddItem,
        onAddSubItem,
        disable,
        enableMenu,
        onChange,
        controlledInput,
        inputName,
        required,
        hideArchive,
        defaultValue,
        toggleButtonRef,
        dragHandleProps,
        isEdit,
    }: ItemContainerProps) => {
    const navigate = useNavigate();
    const { activeEventKey } = useContext(AccordionContext);
    const contextValue = useContext(EditStrategicPlanFormContext);
    const planRuuid = item?.strategic_plan_ruuid;
    const planName = contextValue?.strategicPlan?.name;
    const isClickable = Boolean(planRuuid && planName);

    const handleDrillDownClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            navigate(url);
        }
    };

    const levels = { 0: "first", 1: "second", 2: "third", 3: "fourth" };

    const wrapCount = (statusType: DrillDownStatusType, tooltipLabel: string, className: string, testId: string, children: React.ReactNode) => {
        const url = isClickable ? getActionPlanDrillDownUrl(planRuuid!, statusType, planName) : "";
        const content = <div className={`action-plans-count ${className}`} data-testid={testId}>{children}</div>;
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${item?.id}-${statusType}`} className="tooltip-st-status">{isClickable ? `View action plans: ${tooltipLabel}` : tooltipLabel}</Tooltip>}>
                {isClickable ? (
                    <a href={url} onClick={(e) => handleDrillDownClick(e, url)} className="status-circle-link" aria-label={`View action plans filtered by ${tooltipLabel}`}>
                        {content}
                    </a>
                ) : content}
            </OverlayTrigger>
        );
    };


    const renderControlledInput = () => {
        if (isEdit && controlledInput) {
            return (
                <ControlledInput
                    required={required}
                    className="input-strategic-plans"
                    name={inputName || ""}
                    onInputChange={onChange}
                    itemRef=""
                    defaultValue={defaultValue}
                    placeholder="Plan item - Example"
                />
            );
        } else {
            return <div className={item?.status === "archived" ? "archived-item" : ""}>{item?.name ?? ""}</div>;
        }
    };

    return (
        <div className={`${levels[level]} item-container`}>
            <div className="item-not-status">
                <div className="item-elements-arrow">
                    {item && <button
                        type="button"
                        className={`order_toggle_${eventKey}`}
                        {...dragHandleProps}
                        style={{ background: "transparent", border: "none", cursor: "grab" }}
                    ><ActiveDragIcon />
                    </button>}
                    {level !== 3 && (
                        <CustomToggle
                            level={level}
                            toggleButtonRef={toggleButtonRef}
                            eventKey={eventKey}
                            disabled={item?.status === "archived" ? true : enableMenu ? false : !item?.children?.length}
                        />
                    )}
                    <div className="item-elements-name text-click">
                        <MenuAction
                            onAddItem={onAddItem && onAddItem}
                            onAddSubItem={onAddSubItem && onAddSubItem}
                            disable={item?.status === "archived" ? true : disable}
                        />
                        {renderControlledInput()}
                    </div>
                </div>
                <div className="item-elements-actions">
                    {!hideArchive && (
                        item?.status === "archived" ?
                            <div className={disable ? "drop-icon-disabled" : "unarchive-action"} onClick={disable ? () => { } : onOpenArchive}>Unarchive</div> :
                            <OverlayTrigger overlay={
                                <Tooltip className="tooltip-st-status">
                                    Archive the item
                                </Tooltip>
                            } placement="top">
                                <ArchiveIcon className={disable ? "drop-icon-disabled" : "drop-icon"} onClick={disable ? () => { } : onOpenArchive} />
                            </OverlayTrigger>
                    )}
                    <OverlayTrigger overlay={
                        <Tooltip className="tooltip-st-status">
                            Delete plan item
                        </Tooltip>
                    } placement="top">
                        <DeleteIcon className={disable ? "drop-icon-disabled" : "drop-icon"} onClick={disable ? () => { } : onOpenDelete} />
                    </OverlayTrigger>
                    <div className="vertical-divider" />
                </div>
            </div>
            {wrapCount("open", "Open", "open", "open-item-value", activeEventKey ? item?.action_plan_statuses?.open_time ?? 0 : item?.total_children_action_plan_statuses?.open_time ?? 0)}
            {wrapCount("achieved", "Met", "met", "meet-item-value", activeEventKey ? item?.action_plan_statuses?.closed_goal_met ?? 0 : item?.total_children_action_plan_statuses?.closed_goal_met ?? 0)}
            {wrapCount("unmet", "Unmet", "unmet", "unmet-item-value", activeEventKey ? item?.action_plan_statuses?.closed_goal_not_met ?? 0 : item?.total_children_action_plan_statuses?.closed_goal_not_met ?? 0)}
            {wrapCount("no_date_assigned", "No Date Assigned", "observation", "observation-item-value", activeEventKey ? item?.action_plan_statuses?.no_date_assigned ?? 0 : item?.total_children_action_plan_statuses?.no_date_assigned ?? 0)}
            {wrapCount("overdue", "Overdue", "overdue", "overdue-item-value", activeEventKey ? item?.action_plan_statuses?.open_late ?? 0 : item?.total_children_action_plan_statuses?.open_late ?? 0)}
        </div>
    );
};
